import { BLOCK_TYPE_TO_SEGMENT, ContentBlockType } from '@onesource/schemas';

/**
 * Slug-based content URL builders.
 *
 * Programs content is namespaced under /programs, community feeds under
 * /community-hub, sharing the program slug segment:
 *   /programs/{program}
 *   /programs/{program}/{module}
 *   /programs/{program}/{module}/{lesson}
 *   /community-hub/{program}/{feed}
 *
 * Each helper returns a `{ to, params }` pair for TanStack `<Link>` / `navigate`
 * — `to` is the route pattern (typed against the generated tree), `params`
 * carries the slug values. Program/module slugs derive from the legacy
 * `space.slug` (via appKeyToSlug); lesson slugs from the lesson's `urlSlug`.
 */

/** Content block type -> URL segment, for the item-viewer routes (shared with the API via schemas). */
export { BLOCK_TYPE_TO_SEGMENT };

export const segmentForBlockType = (blockType: string): string | undefined => BLOCK_TYPE_TO_SEGMENT[blockType];

/**
 * Segment for a specific block, which is not always the segment for its type: a
 * finished live session whose replay is available (`isReplay`) is video content,
 * so it routes to the video viewer rather than the event one.
 *
 * `segmentForBlockType` above stays type-only on purpose — the legacy redirect
 * stubs are handed a block type by the server, with no block to inspect, and an
 * old `/event/{id}` URL should still resolve as an event.
 */
export const segmentForBlock = (block: { blockType: string; isReplay?: boolean }): string | undefined =>
  block.isReplay && block.blockType === ContentBlockType.EVENT
    ? BLOCK_TYPE_TO_SEGMENT[ContentBlockType.VIMEO]
    : BLOCK_TYPE_TO_SEGMENT[block.blockType];

export const programLink = (programSlug: string) => ({
  to: '/programs/$programSlug' as const,
  params: { programSlug },
});

export const moduleLink = (programSlug: string, moduleSlug: string) => ({
  to: '/programs/$programSlug/$moduleSlug' as const,
  params: { programSlug, moduleSlug },
});

export const lessonLink = (programSlug: string, moduleSlug: string, lessonSlug: string) => ({
  to: '/programs/$programSlug/$moduleSlug/$lessonSlug' as const,
  params: { programSlug, moduleSlug, lessonSlug },
});

export const communityLink = (programSlug: string, feedSlug: string) => ({
  to: '/community-hub/$programSlug/$feedSlug' as const,
  params: { programSlug, feedSlug },
});
